/**
 * Calendar utilities for S0rted task reminders.
 *
 * Detects time-based phrases inside a step's text (e.g. "every 6 months",
 * "annually", "after 3 weeks"), parses them into a default date + recurrence,
 * and exposes helpers to (a) download an .ics file and (b) open a Google
 * Calendar deep-link with the event pre-filled.
 */

const MONTHS_NUM = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };

/**
 * Returns null if the step text has no time phrase, otherwise:
 *   { whenISO: 'YYYYMMDD', recurrence: 'YEARLY' | 'MONTHLY' | 'WEEKLY' | null, label: '...' }
 */
export function detectReminder(text) {
  if (!text || typeof text !== "string") return null;
  const s = text.toLowerCase();

  // every X months / years / weeks
  let m = s.match(/every\s+(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+(week|month|year)s?\b/);
  if (m) {
    const n = Number(m[1]) || MONTHS_NUM[m[1]] || 1;
    const unit = m[2];
    const d = new Date();
    if (unit === "week") d.setDate(d.getDate() + 7 * n);
    if (unit === "month") d.setMonth(d.getMonth() + n);
    if (unit === "year") d.setFullYear(d.getFullYear() + n);
    return {
      whenISO: toICSDate(d),
      recurrence: unit === "week" ? "WEEKLY" : unit === "month" ? "MONTHLY" : "YEARLY",
      interval: n,
      label: `In ${n} ${unit}${n > 1 ? "s" : ""}, repeating`,
    };
  }

  // annually / yearly
  if (/\b(annually|yearly|each\s+year|every\s+year)\b/.test(s)) {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return { whenISO: toICSDate(d), recurrence: "YEARLY", interval: 1, label: "In 1 year, repeating" };
  }

  // monthly
  if (/\b(monthly|each\s+month|every\s+month)\b/.test(s)) {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return { whenISO: toICSDate(d), recurrence: "MONTHLY", interval: 1, label: "In 1 month, repeating" };
  }

  // weekly
  if (/\b(weekly|each\s+week|every\s+week)\b/.test(s)) {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return { whenISO: toICSDate(d), recurrence: "WEEKLY", interval: 1, label: "In 1 week, repeating" };
  }

  // after X weeks/months/years (one-off, no recurrence)
  m = s.match(/(?:after|in)\s+(\d+|a)\s+(week|month|year)s?\b/);
  if (m) {
    const n = m[1] === "a" ? 1 : Number(m[1]);
    const unit = m[2];
    const d = new Date();
    if (unit === "week") d.setDate(d.getDate() + 7 * n);
    if (unit === "month") d.setMonth(d.getMonth() + n);
    if (unit === "year") d.setFullYear(d.getFullYear() + n);
    return {
      whenISO: toICSDate(d), recurrence: null, interval: 0,
      label: `In ${n} ${unit}${n > 1 ? "s" : ""}`,
    };
  }

  // next year / next month
  if (/\bnext\s+year\b/.test(s)) {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return { whenISO: toICSDate(d), recurrence: null, interval: 0, label: "Next year" };
  }
  if (/\bnext\s+month\b/.test(s)) {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return { whenISO: toICSDate(d), recurrence: null, interval: 0, label: "Next month" };
  }

  return null;
}

function toICSDate(d) {
  // YYYYMMDD all-day format
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

function escapeICS(s) {
  return (s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

export function buildICS({ title, description, whenISO, recurrence, interval = 1 }) {
  const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}@s0rted.io`;
  const dtStamp = new Date().toISOString().replace(/[-:.]/g, "").slice(0, 15) + "Z";
  const nextDay = (() => {
    const d = new Date(`${whenISO.slice(0,4)}-${whenISO.slice(4,6)}-${whenISO.slice(6,8)}`);
    d.setDate(d.getDate() + 1);
    return toICSDate(d);
  })();
  const rrule = recurrence ? `RRULE:FREQ=${recurrence};INTERVAL=${interval}\n` : "";
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//S0rted//Reminder//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;VALUE=DATE:${whenISO}`,
    `DTEND;VALUE=DATE:${nextDay}`,
    `SUMMARY:${escapeICS(title)}`,
    `DESCRIPTION:${escapeICS(description)}`,
    rrule.trim(),
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

export function downloadICS({ title, description, whenISO, recurrence, interval }) {
  const ics = buildICS({ title, description, whenISO, recurrence, interval });
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `s0rted-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40)}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function googleCalendarUrl({ title, description, whenISO, recurrence, interval }) {
  // Google expects "20260601/20260602" for all-day events
  const next = (() => {
    const d = new Date(`${whenISO.slice(0,4)}-${whenISO.slice(4,6)}-${whenISO.slice(6,8)}`);
    d.setDate(d.getDate() + 1);
    return toICSDate(d);
  })();
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    details: description || "",
    dates: `${whenISO}/${next}`,
  });
  if (recurrence) {
    params.append("recur", `RRULE:FREQ=${recurrence};INTERVAL=${interval || 1}`);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
